/**
 * Opportunities, buyer side: requirement gathering, the quotation (choose services/modules, accept, reject or negotiate),
 * and the history. Used by the customer portal and by an agency buying from Quixo; the surface supplies the environment
 * (see env.tsx). Prices are computed by the API; the live total shown while choosing uses the same shared rule (priceForSelection).
 * Layout follows Google / Material placement: header + primary action, a toolbar and divided rows for the list, a main + facts
 * column for the detail, and dialogs (bottom sheets on phones) for every form.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Badge, Button, Dialog, IconChevron, KitProvider, LoadingRegion, SkeletonText, Textarea, Input, Field, Select } from '@qm/ui/kit';
import { formatDateTime } from '@qm/i18n';
import {
  OPPORTUNITY_CURRENCIES, OPPORTUNITY_MODULES, OPPORTUNITY_SERVICES, OPPORTUNITY_STATUSES, OPPORTUNITY_TIMELINES, priceForSelection,
  type OpportunityStatus, type OpportunityView, type Requirements, type SellerOption, type SellerView, type VersionView,
} from '@qm/contracts/opportunities';
import { useCopy, useOpp, type ApiPath, type OppFailure } from './env';
import { STATUS_TONE, fmtMoney, toMinor } from './format';
import { Activity, BackLink, ChipSelect, DialogActions, Empty, Fab, Fact, Facts, IconPlus, SearchField, Section } from './parts';
import './opportunities.css';

type Summary = { id: string; number: string; title: string; status: OpportunityStatus; currency: string; finalTotalMinor: number | null; updatedAt: string; seller?: SellerView };

export function BuyerListScreen() {
  const { t, locale, http, apiBase, links, Screen, variant } = useOpp();
  const copy = useCopy();
  const [rows, setRows] = useState<Summary[] | null>(null);
  const [sellers, setSellers] = useState<SellerOption[]>([]);
  const [error, setError] = useState(false);
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const navigate = useNavigate();
  useEffect(() => { http.get<Summary[]>(apiBase).then(setRows, () => setError(true)); }, [apiBase]);
  // a customer workspace may send its requirements to an agency it works with; an agency always buys from Quixo
  useEffect(() => { if (variant === 'customer') http.get<SellerOption[]>(`${apiBase}/sellers` as ApiPath).then(setSellers, () => undefined); }, [apiBase, variant]);

  const create = async (r: Requirements, sellerPartnerId?: string) => {
    const v = await http.send<OpportunityView>('POST', apiBase, { ...r, ...(sellerPartnerId ? { sellerPartnerId } : {}) });
    void navigate(links.detail(v.id));
  };
  const shown = useMemo(() => {
    const n = query.trim().toLowerCase();
    return (rows ?? []).filter((r) => (!status || r.status === status) && (!n || [r.number, r.title, r.seller?.name].some((x) => x?.toLowerCase().includes(n))));
  }, [rows, query, status]);

  const open = () => setCreating(true);
  return (
    <KitProvider locale={locale}>
      <Screen title={copy.text('title')} subtitle={copy.text('intro')} testId="opportunities-buyer-list"
        actions={<Button variant="accent" className="qop-desk-only" iconStart={<IconPlus />} onClick={open}>{copy.text('new')}</Button>}>
        <div className="qop">
          {error ? <Empty text={t('shell.error.load')} />
            : rows === null ? <LoadingRegion label={t('common.loading')}><SkeletonText lines={4} /></LoadingRegion>
              : rows.length === 0 ? <Empty text={copy.text('empty')} action={<Button variant="accent" iconStart={<IconPlus />} onClick={open}>{copy.text('new')}</Button>} />
                : (
                  <>
                    <div className="qop-toolbar">
                      <SearchField label={t('opportunities.list.search')} value={query} onChange={setQuery} />
                      <ChipSelect label={t('opportunities.admin.status')} value={status} onChange={setStatus}>
                        <option value="">{t('opportunities.admin.anyStatus')}</option>
                        {OPPORTUNITY_STATUSES.map((s) => <option key={s} value={s}>{t(`opportunities.status.${s}`)}</option>)}
                      </ChipSelect>
                      <span className="qop-count" role="status">{t('opportunities.list.count', { count: String(shown.length) })}</span>
                    </div>
                    {shown.length === 0 ? <Empty text={t('opportunities.list.noMatch')} /> : (
                      <div className="qop-list">
                        <div className="qop-rowhead" aria-hidden="true">
                          <span className="qop-row__num">{t('opportunities.admin.col.number')}</span><span className="qop-row__title">{t('opportunities.admin.col.title')}</span><span className="qop-row__status">{t('opportunities.admin.col.status')}</span>
                          <span className="qop-row__buyer">{t('opportunities.seller')}</span><span className="qop-row__price">{t('opportunities.admin.col.price')}</span><span className="qop-row__upd">{t('opportunities.admin.col.updated')}</span>
                        </div>
                        <ul className="qop-rows">
                          {shown.map((r) => (
                            <li key={r.id}>
                              <Link to={links.detail(r.id)} className="qop-row">
                                <span className="qop-row__meta"><span className="qop-row__num" dir="ltr">{r.number}</span>{r.seller ? <span className="qop-row__buyer"><bdi>{r.seller.name}</bdi></span> : null}</span>
                                <span className="qop-row__title">{r.title}</span>
                                <span className="qop-row__status"><Badge tone={STATUS_TONE[r.status]}>{t(`opportunities.status.${r.status}`)}</Badge></span>
                                <span className="qop-row__price" dir="ltr">{r.finalTotalMinor !== null ? fmtMoney(locale, r.currency, r.finalTotalMinor) : '—'}</span>
                                <span className="qop-row__upd">{formatDateTime(locale, r.updatedAt, { style: 'date' })}</span>
                                <span className="qop-row__go" aria-hidden="true"><IconChevron width={18} height={18} /></span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}
        </div>
        <Fab label={copy.text('new')} onClick={open} />
        <Dialog open={creating} onClose={() => setCreating(false)} size="lg" title={t('opportunities.form.title')}>
          <RequirementsForm submitLabel={t('opportunities.form.saveDraft')} sellers={sellers} onSave={create} onCancel={() => setCreating(false)} />
        </Dialog>
      </Screen>
    </KitProvider>
  );
}

const EMPTY: Requirements = { title: '', services: [], modules: [], description: '', goals: '', currency: 'AED' };

function ChoiceChips({ legend, items, selected, label, onToggle }: { legend: string; items: readonly string[]; selected: string[]; label: (v: string) => string; onToggle: (v: string) => void }) {
  return (
    <fieldset className="qop-choices">
      <legend>{legend}</legend>
      <div>
        {items.map((v) => (
          <label key={v} className="qop-choice"><input type="checkbox" checked={selected.includes(v)} onChange={() => onToggle(v)} /><span>{label(v)}</span></label>
        ))}
      </div>
    </fieldset>
  );
}

export function RequirementsForm({ initial = EMPTY, submitLabel, sellers = [], onSave, onCancel }: { initial?: Requirements; submitLabel: string; sellers?: SellerOption[]; onSave: (r: Requirements, sellerPartnerId?: string) => Promise<void>; onCancel?: () => void }) {
  const { t } = useOpp();
  const [r, setR] = useState<Requirements>(initial);
  const [seller, setSeller] = useState('');
  const [budget, setBudget] = useState(initial.budgetMinor !== undefined ? String(initial.budgetMinor / 100) : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const toggle = (key: 'services' | 'modules', v: string) =>
    setR((p) => ({ ...p, [key]: (p[key] as string[]).includes(v) ? (p[key] as string[]).filter((x) => x !== v) : [...(p[key] as string[]), v] }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || r.title.trim().length < 3) return;
    setBusy(true); setError(false);
    const b = toMinor(budget);
    const { budgetMinor: _drop, ...rest } = r;
    try { await onSave({ ...rest, ...(b !== null ? { budgetMinor: b } : {}) }, seller || undefined); } catch { setError(true); } finally { setBusy(false); }
  };

  return (
    <form className="qop-dform" onSubmit={(e) => void submit(e)}>
      {sellers.length > 1 ? (
        <Field label={t('opportunities.form.sendTo')} hint={t('opportunities.form.sendToHint')}>{(p) => (
          <Select id={p.id} aria-describedby={p.describedBy} value={seller} onChange={(e) => setSeller(e.target.value)}>
            {sellers.map((x) => <option key={x.partnerId ?? 'quixo'} value={x.partnerId ?? ''}>{x.name}</option>)}
          </Select>
        )}</Field>
      ) : null}
      <Field label={t('opportunities.form.name')} required>{(p) => <Input id={p.id} aria-describedby={p.describedBy} required minLength={3} maxLength={200} value={r.title} onChange={(e) => setR({ ...r, title: e.target.value })} />}</Field>
      <ChoiceChips legend={t('opportunities.form.services')} items={OPPORTUNITY_SERVICES} selected={r.services} label={(s) => t(`opportunities.service.${s}`)} onToggle={(v) => toggle('services', v)} />
      <ChoiceChips legend={t('opportunities.form.modules')} items={OPPORTUNITY_MODULES} selected={r.modules} label={(m) => t(`opportunities.module.${m}`)} onToggle={(v) => toggle('modules', v)} />
      <Field label={t('opportunities.form.description')} hint={t('opportunities.form.descriptionHint')}>{(p) => <Textarea id={p.id} aria-describedby={p.describedBy} rows={4} maxLength={4000} value={r.description} onChange={(e) => setR({ ...r, description: e.target.value })} />}</Field>
      <Field label={t('opportunities.form.goals')}>{(p) => <Textarea id={p.id} rows={2} maxLength={2000} value={r.goals} onChange={(e) => setR({ ...r, goals: e.target.value })} />}</Field>
      <Field label={t('opportunities.form.timeline')}>{(p) => (
        <Select id={p.id} value={r.timeline ?? ''} onChange={(e) => setR({ ...r, timeline: (e.target.value || undefined) as Requirements['timeline'] })}>
          <option value="">{t('opportunities.form.choose')}</option>
          {OPPORTUNITY_TIMELINES.map((x) => <option key={x} value={x}>{t(`opportunities.timeline.${x}`)}</option>)}
        </Select>
      )}</Field>
      <div className="qop-pair">
        <Field label={t('opportunities.form.currency')}>{(p) => (
          <Select id={p.id} value={r.currency} onChange={(e) => setR({ ...r, currency: e.target.value as Requirements['currency'] })}>
            {OPPORTUNITY_CURRENCIES.map((x) => <option key={x} value={x}>{x}</option>)}
          </Select>
        )}</Field>
        <Field label={t('opportunities.form.budget')} hint={t('opportunities.form.budgetHint')}>{(p) => <Input id={p.id} aria-describedby={p.describedBy} inputMode="decimal" dir="ltr" value={budget} onChange={(e) => setBudget(e.target.value)} />}</Field>
      </div>
      <div className="qop-pair">
        <Field label={t('opportunities.form.contactName')}>{(p) => <Input id={p.id} maxLength={200} value={r.contactName ?? ''} onChange={(e) => setR({ ...r, contactName: e.target.value || undefined })} />}</Field>
        <Field label={t('opportunities.form.contactPhone')}>{(p) => <Input id={p.id} maxLength={40} dir="ltr" value={r.contactPhone ?? ''} onChange={(e) => setR({ ...r, contactPhone: e.target.value || undefined })} />}</Field>
      </div>
      {error ? <p role="alert" className="qop-error">{t('opportunities.error.save')}</p> : null}
      {onCancel ? <DialogActions cancelLabel={t('opportunities.cancel')} onCancel={onCancel} submitLabel={submitLabel} busy={busy} />
        : <div className="qop-dactions"><Button type="submit" variant="accent" loading={busy}>{submitLabel}</Button></div>}
    </form>
  );
}

function errorKey(f: OppFailure): string {
  if (!f.status) return 'opportunities.error.generic';
  const { reason } = f;
  if (reason === 'incomplete') return 'opportunities.error.incomplete';
  if (reason === 'required_items') return 'opportunities.error.requiredItems';
  if (reason === 'not_a_discount') return 'opportunities.error.notDiscount';
  if (reason === 'expired') return 'opportunities.error.expired';
  if (reason === 'seller_not_available') return 'opportunities.error.sellerNotAvailable';
  if (f.status === 409) return 'opportunities.error.changed';
  if (f.status === 403) return 'opportunities.error.forbidden';
  return 'opportunities.error.generic';
}

export function BuyerDetailScreen() {
  const { t, locale, http, apiBase, links, Screen, failure } = useOpp();
  const { id = '' } = useParams();
  const [opp, setOpp] = useState<OpportunityView | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'error'>('loading');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const path = `${apiBase}/${encodeURIComponent(id)}` as ApiPath;

  useEffect(() => {
    http.get<OpportunityView>(path).then((v) => { setOpp(v); setState('ready'); }, (e: unknown) => setState(failure(e).status === 404 ? 'missing' : 'error'));
  }, [path]);

  const act = async (fn: () => Promise<OpportunityView>, ok: string) => {
    setBusy(true); setMsg(null);
    try { setOpp(await fn()); setMsg(ok); } catch (e) { setMsg(errorKey(failure(e))); } finally { setBusy(false); }
  };
  const post = (suffix: string, body?: unknown) => http.send<OpportunityView>('POST', `${path}${suffix}` as ApiPath, body);
  /** The quotation response: errors reach the dialog or the quotation, which shows them where the person is looking. */
  const respond = async (b: Record<string, unknown>) => {
    setBusy(true); setMsg(null);
    try { setOpp(await post('/respond', b)); setMsg(`opportunities.responded.${b.action as string}`); } finally { setBusy(false); }
  };

  if (state === 'missing' || state === 'error') {
    return <Screen title={t('opportunities.title')} testId="opportunity-buyer-detail"><p role="alert" className="qop-error">{state === 'missing' ? t('opportunities.error.notFound') : t('shell.error.load')}</p></Screen>;
  }
  if (!opp) return <Screen title={t('opportunities.title')} testId="opportunity-buyer-detail"><LoadingRegion label={t('common.loading')}><SkeletonText lines={6} /></LoadingRegion></Screen>;

  const r = opp.requirements;
  const current = opp.versions.at(-1);
  const seller = opp.seller.name;
  const draft = opp.status === 'draft';
  return (
    <KitProvider locale={locale}>
      <Screen eyebrow={opp.number} title={r.title} subtitle={t(`opportunities.statusHelp.${opp.status}`, { seller })} testId="opportunity-buyer-detail"
        actions={(
          <>
            <Badge tone={STATUS_TONE[opp.status]}>{t(`opportunities.status.${opp.status}`)}</Badge>
            {draft ? <Button variant="secondary" onClick={() => setEditing(true)}>{t('opportunities.edit')}</Button> : null}
            {draft ? <Button variant="accent" loading={busy} onClick={() => void act(() => post('/submit'), 'opportunities.submitted')}>{t('opportunities.submit')}</Button> : null}
          </>
        )}>
        <div className="qop">
          <BackLink to={links.list}>{t('opportunities.back')}</BackLink>
          {msg ? <p role="status" className="qop-msg">{t(msg, { seller })}</p> : null}
          <div className="qop-detail">
            <div className="qop-main">
              {draft ? <p className="qop-muted">{t('opportunities.submitHelp', { seller })}</p> : null}
              {!draft && current ? <Quotation opp={opp} version={current} busy={busy} onRespond={respond} /> : null}
              {opp.status === 'accepted' ? <p className="qop-final">{t('opportunities.finalPrice', { price: fmtMoney(locale, opp.currency, opp.finalTotalMinor) })}</p> : null}
              {opp.status === 'rejected' && opp.closeReason ? <p className="qop-muted">{t('opportunities.closeReason', { reason: opp.closeReason })}</p> : null}
              <RequirementsSummary r={r} />
              <Timeline opp={opp} />
            </div>
            <aside className="qop-side" aria-label={t('opportunities.facts')}>
              <h2 className="qop-side__title">{t('opportunities.facts')}</h2>
              <Facts>
                <Fact label={t('opportunities.seller')}><bdi>{seller}</bdi></Fact>
                {opp.accountManagerName ? <Fact label={t('opportunities.admin.accountManager')}>{opp.accountManagerName}</Fact> : null}
                {r.budgetMinor !== undefined ? <Fact label={t('opportunities.form.budget')} dir="ltr">{fmtMoney(locale, r.currency, r.budgetMinor)}</Fact> : null}
                {opp.customerRequiredPriceMinor !== null ? <Fact label={t('opportunities.admin.required')} dir="ltr">{fmtMoney(locale, opp.currency, opp.customerRequiredPriceMinor)}</Fact> : null}
                {current?.validUntil ? <Fact label={t('opportunities.admin.validUntil')}>{formatDateTime(locale, current.validUntil, { style: 'date' })}</Fact> : null}
              </Facts>
            </aside>
          </div>
        </div>
        <Dialog open={editing} onClose={() => setEditing(false)} size="lg" title={t('opportunities.edit')}>
          <RequirementsForm initial={r} submitLabel={t('opportunities.form.saveDraft')} onCancel={() => setEditing(false)}
            onSave={async (next) => { setOpp(await http.send<OpportunityView>('PUT', `${path}/requirements` as ApiPath, next)); setMsg('opportunities.saved'); setEditing(false); }} />
        </Dialog>
      </Screen>
    </KitProvider>
  );
}

export function RequirementsSummary({ r }: { r: Requirements }) {
  const { t, locale } = useOpp();
  const rows: [string, ReactNodeLike, boolean?][] = [
    [t('opportunities.form.services'), r.services.map((s) => t(`opportunities.service.${s}`)).join(', ')],
    [t('opportunities.form.modules'), r.modules.map((m) => t(`opportunities.module.${m}`)).join(', ')],
    [t('opportunities.form.description'), r.description, true],
    [t('opportunities.form.goals'), r.goals, true],
    [t('opportunities.form.timeline'), r.timeline ? t(`opportunities.timeline.${r.timeline}`) : ''],
    [t('opportunities.form.budget'), r.budgetMinor !== undefined ? fmtMoney(locale, r.currency, r.budgetMinor) : ''],
  ];
  return (
    <Section id="opp-req" title={t('opportunities.requirements')}>
      <dl className="qop-dl">
        {rows.filter(([, v]) => v).map(([k, v, pre]) => <div key={k}><dt>{k}</dt><dd className={pre ? 'qop-pre' : undefined}>{v}</dd></div>)}
      </dl>
    </Section>
  );
}
type ReactNodeLike = string;

function CounterForm({ currency, busy, error, onSubmit, onCancel, canSend }: { currency: string; busy: boolean; error: string | null; canSend: boolean; onSubmit: (p: { requiredPriceMinor: number; budgetMinor?: number; note: string }) => void; onCancel: () => void }) {
  const { t } = useOpp();
  const [required, setRequired] = useState('');
  const [budget, setBudget] = useState('');
  const [note, setNote] = useState('');
  const requiredMinor = toMinor(required);
  const budgetMinor = toMinor(budget);
  return (
    <form className="qop-dform" onSubmit={(e) => { e.preventDefault(); if (requiredMinor !== null) onSubmit({ requiredPriceMinor: requiredMinor, ...(budgetMinor !== null ? { budgetMinor } : {}), note }); }}>
      <Field label={t('opportunities.requiredPrice', { currency })} required>{(p) => <Input id={p.id} required inputMode="decimal" dir="ltr" value={required} onChange={(e) => setRequired(e.target.value)} />}</Field>
      <Field label={t('opportunities.budget', { currency })}>{(p) => <Input id={p.id} inputMode="decimal" dir="ltr" value={budget} onChange={(e) => setBudget(e.target.value)} />}</Field>
      <Field label={t('opportunities.note')}>{(p) => <Textarea id={p.id} rows={3} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} />}</Field>
      {error ? <p role="alert" className="qop-error">{error}</p> : null}
      <DialogActions cancelLabel={t('opportunities.cancel')} onCancel={onCancel} submitLabel={t('opportunities.sendCounter')} busy={busy} disabled={requiredMinor === null || !canSend} />
    </form>
  );
}

function RejectForm({ busy, error, onSubmit, onCancel }: { busy: boolean; error: string | null; onSubmit: (reason: string) => void; onCancel: () => void }) {
  const { t } = useOpp();
  const [reason, setReason] = useState('');
  return (
    <form className="qop-dform" onSubmit={(e) => { e.preventDefault(); if (reason.trim().length >= 3) onSubmit(reason.trim()); }}>
      <Field label={t('opportunities.rejectReason')} required>{(p) => <Textarea id={p.id} required minLength={3} rows={3} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} />}</Field>
      {error ? <p role="alert" className="qop-error">{error}</p> : null}
      <DialogActions cancelLabel={t('opportunities.cancel')} onCancel={onCancel} submitLabel={t('opportunities.confirmReject')} busy={busy} disabled={reason.trim().length < 3} danger />
    </form>
  );
}

export function Quotation({ opp, version, busy, onRespond }: { opp: OpportunityView; version: VersionView; busy: boolean; onRespond: (b: Record<string, unknown>) => Promise<void> }) {
  const { t, locale, failure } = useOpp();
  const open = opp.status === 'quoted';
  const initial = opp.selection.length ? opp.selection : version.basisItemIds;
  const [selected, setSelected] = useState<string[]>(initial);
  const [dlg, setDlg] = useState<'none' | 'counter' | 'reject'>('none');
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { setSelected(opp.selection.length ? opp.selection.filter((x) => version.items.some((i) => i.id === x)) : version.basisItemIds); }, [version.id]);
  const total = useMemo(() => priceForSelection(version, selected), [version, selected]);
  const list = useMemo(() => version.items.filter((i) => selected.includes(i.id)).reduce((s, i) => s + i.listPriceMinor, 0), [version, selected]);
  const money = (m: number | null) => fmtMoney(locale, opp.currency, m);
  const toggle = (itemId: string) => setSelected((s) => (s.includes(itemId) ? s.filter((x) => x !== itemId) : [...s, itemId]));

  const run = async (b: Record<string, unknown>) => {
    setErr(null);
    try { await onRespond(b); setDlg('none'); } catch (e) { setErr(t(errorKey(failure(e)))); }
  };
  const close = () => { setDlg('none'); setErr(null); };

  return (
    <Section id="opp-quote" title={t('opportunities.quotation', { version: version.version })}>
      {version.message ? <p className="qop-pre qop-lead">{version.message}</p> : null}
      {version.validUntil ? <p className="qop-muted">{t('opportunities.validUntil', { date: formatDateTime(locale, version.validUntil, { style: 'date' }) })}</p> : null}
      <fieldset className="qop-pick" disabled={!open || busy}>
        <legend>{open ? t('opportunities.chooseItems') : t('opportunities.items')}</legend>
        <ul>
          {version.items.map((i) => (
            <li key={i.id}>
              <label className="qop-pickrow">
                <input type="checkbox" checked={selected.includes(i.id)} disabled={i.required} onChange={() => toggle(i.id)} />
                <span className="qop-pickrow__text">
                  <span className="qop-pickrow__name">{i.name}</span>
                  <span className="qop-pickrow__meta">{t(`opportunities.kind.${i.kind}`)}{i.required ? ` · ${t('opportunities.required')}` : ''}{i.description ? ` · ${i.description}` : ''}</span>
                </span>
                <span className="qop-price" dir="ltr">{money(i.listPriceMinor)}</span>
              </label>
            </li>
          ))}
        </ul>
      </fieldset>
      <div className="qop-summary">
        <dl>
          <div><dt>{t('opportunities.listTotal')}</dt><dd dir="ltr">{money(list)}</dd></div>
          {total < list ? <div><dt>{t('opportunities.discount')}</dt><dd dir="ltr">−{money(list - total)}</dd></div> : null}
          <div className="qop-summary__grand"><dt>{t('opportunities.yourPrice')}</dt><dd dir="ltr">{money(total)}</dd></div>
        </dl>
        {open ? (
          <div className="qop-summary__actions">
            <Button variant="ghost" disabled={busy} onClick={() => { setErr(null); setDlg('reject'); }}>{t('opportunities.reject')}</Button>
            <Button variant="secondary" disabled={busy} onClick={() => { setErr(null); setDlg('counter'); }}>{t('opportunities.negotiate')}</Button>
            <Button variant="accent" loading={busy} disabled={!selected.length} onClick={() => void run({ action: 'accept', versionId: version.id, selectedItemIds: selected })}>{t('opportunities.accept')}</Button>
          </div>
        ) : null}
        {open && err && dlg === 'none' ? <p role="alert" className="qop-error">{err}</p> : null}
      </div>
      {open ? (
        <>
          <Dialog open={dlg === 'counter'} onClose={close} title={t('opportunities.negotiate')} description={t('opportunities.counterHelp')}>
            <CounterForm currency={opp.currency} busy={busy} error={err} canSend={selected.length > 0} onCancel={close}
              onSubmit={(p) => void run({ action: 'counter', versionId: version.id, selectedItemIds: selected, ...p })} />
          </Dialog>
          <Dialog open={dlg === 'reject'} onClose={close} title={t('opportunities.reject')}>
            <RejectForm busy={busy} error={err} onCancel={close} onSubmit={(reason) => void run({ action: 'reject', versionId: version.id, reason })} />
          </Dialog>
        </>
      ) : null}
    </Section>
  );
}

export function Timeline({ opp, staff = false }: { opp: OpportunityView; staff?: boolean }) {
  const { t } = useOpp();
  if (!opp.events.length) return null;
  return <Section id="opp-history" title={t('opportunities.timeline')}><Activity opp={opp} actor={staff ? (e) => e.actorId ?? null : undefined} /></Section>;
}

